import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Zap,
  Clock,
  TrendingUp,
  Shield,
  Settings,
  FileText,
  Mail,
  RefreshCw,
  Target,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';

import SEO from '../components/SEO';

const ProzessanalysePage = () => (
  <div className="pt-20">
    <SEO
      title="KI-Prozessautomatisierung Magdeburg – Kostenlose Prozessanalyse"
      description="Kostenlose Prozessanalyse und KI-gestützte Automatisierung für Unternehmen. Bis zu 70% Zeitersparnis durch intelligente Automatisierung. LAIzyones Magdeburg & Braunschweig."
      path="/prozessanalyse"
      keywords="KI Prozessautomatisierung, Prozessanalyse KI, Geschäftsprozesse automatisieren, KI Automatisierung Unternehmen, Prozessoptimierung KI Magdeburg, Workflow Automatisierung, RPA KI, Kosten senken KI"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'Prozessanalyse & Automatisierung' },
      ]}
      serviceSchema={{
        name: 'KI-Prozessanalyse & Automatisierung',
        description: 'Kostenlose Prozessanalyse und KI-gestützte Automatisierung repetitiver Aufgaben für messbare Zeit- und Kostenersparnis in Unternehmen.',
        offers: 'Kostenlose Erstanalyse',
      }}
      faqSchema={[
        { question: 'Was kostet eine KI-Prozessanalyse?', answer: 'Die erste Prozessanalyse ist bei LAIzyones komplett kostenlos und unverbindlich. Wir identifizieren dabei konkrete Automatisierungspotenziale und berechnen den zu erwartenden ROI für Ihr Unternehmen.' },
        { question: 'Welche Prozesse lassen sich mit KI automatisieren?', answer: 'Grundsätzlich alle repetitiven, regelbasierten oder datenintensiven Prozesse: E-Mail-Bearbeitung, Datenerfassung, Dokumentenverarbeitung, Qualitätskontrolle, Reporting und viele mehr.' },
        { question: 'Funktioniert KI-Automatisierung auch in meiner Branche?', answer: 'Ja. Wir haben Erfahrung in Branchen wie Fertigung, Handel, Finanzdienstleistungen, Gesundheitswesen und Logistik. Die Automatisierungspotenziale sind branchenübergreifend enorm.' },
        { question: 'Wie lange dauert die Umsetzung?', answer: 'Einfache Automatisierungen sind oft in wenigen Tagen produktiv. Komplexere Projekte benötigen 2–6 Wochen. In jedem Fall erhalten Sie vor Projektstart einen klaren Zeitplan.' },
      ]}
    />
    <HeroSection />
    <BenefitsSection />
    <UseCasesSection />
    <ProcessSection />
    <FAQSection />
    <CTASection />
  </div>
);

const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">KI-Prozessautomatisierung</div>
        <div className="reveal-container">
          <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
            Prozessanalyse & <span className="gradient-text">Automatisierung mit KI</span>
          </h1>
        </div>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up mb-8">
          Wir analysieren Ihre Geschäftsprozesse kostenfrei und automatisieren repetitive Aufgaben
          mit künstlicher Intelligenz — für messbare Zeit- und Kostenersparnis.
          Mit Sitz in Magdeburg und Braunschweig, für Unternehmen deutschlandweit.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up">
          <Link to="/#kontakt" className="btn-primary text-base">
            <span>Kostenlose Prozessanalyse</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </Link>
          <Link to="/produkte" className="btn-secondary text-base inline-flex items-center justify-center gap-2">
            Unsere KI-Produkte
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const BenefitsSection = () => {
  const benefits = [
    { icon: Clock, title: 'Bis zu 70% Zeitersparnis', description: 'Automatisieren Sie repetitive Aufgaben wie Dateneingabe, E-Mail-Bearbeitung und Berichterstellung. Ihre Mitarbeiter können sich auf wertschöpfende Tätigkeiten konzentrieren.' },
    { icon: TrendingUp, title: 'Kosten nachhaltig senken', description: 'Reduzieren Sie operative Kosten durch intelligente Automatisierung. KI-Tools zur Prozessoptimierung für KMU amortisieren sich oft innerhalb weniger Monate.' },
    { icon: Shield, title: '100% DSGVO-konform', description: 'Alle Daten werden in Deutschland verarbeitet. Unsere KI-Lösungen erfüllen die höchsten Datenschutzstandards — garantiert und zertifiziert.' },
    { icon: Zap, title: 'Schnelle Implementierung', description: 'Von der Analyse bis zur produktiven KI-Lösung in 2–6 Wochen. Keine monatelangen IT-Projekte, sondern schnelle, messbare Ergebnisse.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Ihre Vorteile</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Warum Unternehmen auf <span className="gradient-text">KI-Prozessautomatisierung</span> setzen
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Unternehmen, die KI-gestützte Prozessautomatisierung einsetzen, steigern ihre Effizienz
            um durchschnittlich 40% und senken Fehlerquoten um bis zu 90%.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="card group animate-card" data-testid={`benefit-${i}`}>
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-5 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
                  <Icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900 mb-2">{b.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{b.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const UseCasesSection = () => {
  const cases = [
    { icon: Mail, title: 'E-Mail-Automatisierung', desc: 'KI liest, kategorisiert und beantwortet E-Mails automatisch. Ideal für Kundenservice, Bestelleingang und Support-Anfragen.' },
    { icon: FileText, title: 'Dokumentenverarbeitung', desc: 'Automatische Extraktion von Daten aus Rechnungen, Verträgen und Formularen. OCR + KI für fehlerfreie Datenerfassung.' },
    { icon: BarChart3, title: 'Reporting & Analyse', desc: 'KI-gestützte Berichte und Dashboards, die automatisch erstellt werden. Echtzeitdaten statt manueller Excel-Arbeit.' },
    { icon: RefreshCw, title: 'Workflow-Automatisierung', desc: 'Verknüpfen Sie bestehende Systeme intelligent. Automatische Datenübertragung zwischen CRM, ERP und weiteren Tools.' },
    { icon: Settings, title: 'Qualitätskontrolle', desc: 'KI erkennt Muster und Anomalien in Produktionsdaten — für frühzeitige Fehlererkennung und gleichbleibende Qualität.' },
    { icon: Target, title: 'Predictive Maintenance', desc: 'Vorhersage von Wartungsbedarf und Ausfallrisiken durch KI-Analyse von Maschinendaten und Sensorwerten.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Anwendungsbereiche</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Prozesse, die wir mit KI <span className="gradient-text">automatisieren</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Individuelle KI-Lösungen für Unternehmen — zugeschnitten auf Ihre branchenspezifischen Abläufe.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="card text-center animate-card">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-5">
                  <Icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Kostenlose Prozessanalyse', desc: 'Wir analysieren Ihre bestehenden Abläufe und identifizieren Automatisierungspotenziale — komplett kostenfrei und unverbindlich.' },
    { num: '02', title: 'Konzept & Strategie', desc: 'Wir erstellen ein maßgeschneidertes Automatisierungskonzept mit klarem ROI-Nachweis und Zeitplan.' },
    { num: '03', title: 'Entwicklung & Test', desc: 'Wir entwickeln Ihre KI-Lösung und testen sie gründlich in einer sicheren Umgebung.' },
    { num: '04', title: 'Integration & Schulung', desc: 'Nahtlose Integration in Ihre bestehende IT-Landschaft plus Schulung Ihres Teams.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unser Vorgehen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            In 4 Schritten zur <span className="gradient-text">automatisierten Zukunft</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="card text-center animate-card relative">
              <span className="font-['Space_Grotesk'] font-bold text-3xl gradient-text mb-3 block">{s.num}</span>
              <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 text-emerald-300">
                  <ChevronRight className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'Was kostet eine KI-Prozessanalyse?', a: 'Die erste Prozessanalyse ist bei LAIzyones komplett kostenlos und unverbindlich. Wir identifizieren dabei konkrete Automatisierungspotenziale und berechnen den zu erwartenden ROI für Ihr Unternehmen.' },
    { q: 'Welche Prozesse lassen sich mit KI automatisieren?', a: 'Grundsätzlich alle repetitiven, regelbasierten oder datenintensiven Prozesse: E-Mail-Bearbeitung, Datenerfassung, Dokumentenverarbeitung, Qualitätskontrolle, Reporting und viele mehr. In der kostenlosen Analyse identifizieren wir die größten Potenziale.' },
    { q: 'Funktioniert KI-Automatisierung auch in meiner Branche?', a: 'Ja. Wir haben Erfahrung in Branchen wie Fertigung, Handel, Finanzdienstleistungen, Gesundheitswesen und Logistik. Die Automatisierungspotenziale sind branchenübergreifend enorm.' },
    { q: 'Wie lange dauert die Umsetzung?', a: 'Einfache Automatisierungen sind oft in wenigen Tagen produktiv. Komplexere Projekte benötigen 2–6 Wochen. In jedem Fall erhalten Sie vor Projektstart einen klaren Zeitplan.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="badge mb-4 fade-up"><HelpCircle className="w-3.5 h-3.5" /> Häufige Fragen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            FAQ zur <span className="gradient-text">KI-Prozessautomatisierung</span>
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="card overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left group">
                <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 pr-4 group-hover:text-emerald-600 transition-colors">{faq.q}</h3>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-emerald-500' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Effizienz steigern mit <span className="text-emerald-400">KI-Automatisierung</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg mb-8">
            Starten Sie mit einer kostenlosen Prozessanalyse. Wir zeigen Ihnen konkret,
            wo KI in Ihrem Unternehmen den größten Hebel hat — unverbindlich und DSGVO-konform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#kontakt" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all">
              Kostenlose Analyse starten <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/ki-tool-entwicklung" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/5 transition-all">
              KI-Tool-Entwicklung
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default ProzessanalysePage;
